import { getStoredRelays } from '../utils/relay';
import {
  getMessageByHash,
  getSignatures,
  getKeys,
} from '../utils/relay';
import { tryDecryptWithKeys } from './syncDecrypt';
import { updateGraphFromMessage } from './syncUpdateGraph';
import { validateDecryptedMessage } from './syncValidate';
import type { LocalIdentity } from '../types/identity';
import type { GraphResolver } from './graphResolver';
import type { MsgChiffre, MsgSignature, MsgCle } from '../types/message';

/**
 * Types of objects that can be detected from relay events.
 */
export type RelayObjectType =
  | 'message'
  | 'signature'
  | 'key'
  | 'contrat'
  | 'champ'
  | 'action'
  | 'membre'
  | 'pair'
  | 'service'
  | 'unknown';

/**
 * Event emitted when a new hash is detected on relays.
 */
export interface RelayHashEvent {
  hash: string;
  relay: string;
  timestamp: number;
  objectType?: RelayObjectType;
  source?: 'polling' | 'manual';
}

/**
 * Callback for hash events.
 */
export type HashEventListener = (event: RelayHashEvent) => void;

/**
 * Options for processing a hash.
 */
export interface ProcessHashOptions {
  fetchMessage?: boolean;
  fetchSignatures?: boolean;
  fetchKeys?: boolean;
  decryptAndUpdateGraph?: boolean;
}

/**
 * Result of processing a hash.
 */
export interface ProcessHashResult {
  hash: string;
  messageFetched: boolean;
  messageDecrypted: boolean;
  graphUpdated: boolean;
  signaturesFetched: number;
  keysFetched: number;
  error?: string;
}

/**
 * Backoff strategy for mobile network optimization.
 */
interface BackoffState {
  attempts: number;
  lastAttempt: number;
  baseDelay: number;
  maxDelay: number;
}

/**
 * Service for handling relay notifications and automatic fetching.
 * Pull-based model (polling) only: HTTP REST calls, no WebSocket.
 * Optimized for mobile with backoff and reconnection strategies.
 */
export class RelayNotificationService {
  private readonly graphResolver: GraphResolver;
  private readonly identity: LocalIdentity | null;
  private hashListeners: Set<HashEventListener> = new Set();
  private isPolling: boolean = false;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private backoffStates: Map<string, BackoffState> = new Map();
  private readonly maxRetries: number = 5;
  private readonly baseBackoffMs: number = 1000;
  private readonly maxBackoffMs: number = 60000;
  private isOnline: boolean = true;

  constructor(
    graphResolver: GraphResolver,
    identity?: LocalIdentity | null,
  ) {
    this.graphResolver = graphResolver;
    this.identity = identity ?? null;
    this.setupNetworkListeners();
  }

  /**
   * Setup network online/offline listeners for mobile.
   */
  private setupNetworkListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.isOnline = navigator.onLine ?? true;

    window.addEventListener('online', () => {
      this.isOnline = true;
      console.info('[RelayNotifications] Network online, resuming operations');
      this.resumeOperations();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.warn('[RelayNotifications] Network offline, pausing operations');
      this.pauseOperations();
    });
  }

  /**
   * Resume operations after network reconnection.
   */
  private resumeOperations(): void {
    // Reset backoff states
    this.backoffStates.clear();
    // Polling will continue automatically on next interval
  }

  /**
   * Pause operations when network is offline.
   */
  private pauseOperations(): void {
    // Polling will fail gracefully when offline
  }

  /**
   * Calculate backoff delay for retry.
   */
  private getBackoffDelay(endpoint: string): number {
    const state = this.backoffStates.get(endpoint) ?? {
      attempts: 0,
      lastAttempt: 0,
      baseDelay: this.baseBackoffMs,
      maxDelay: this.maxBackoffMs,
    };

    const delay = Math.min(
      state.baseDelay * Math.pow(2, state.attempts),
      state.maxDelay,
    );
    return delay;
  }

  /**
   * Record failed attempt and update backoff state.
   */
  private recordFailure(endpoint: string): void {
    const state = this.backoffStates.get(endpoint) ?? {
      attempts: 0,
      lastAttempt: Date.now(),
      baseDelay: this.baseBackoffMs,
      maxDelay: this.maxBackoffMs,
    };

    state.attempts += 1;
    state.lastAttempt = Date.now();
    this.backoffStates.set(endpoint, state);
  }

  /**
   * Record successful attempt and reset backoff.
   */
  private recordSuccess(endpoint: string): void {
    this.backoffStates.delete(endpoint);
  }

  /**
   * Check if should retry based on backoff state.
   */
  private shouldRetry(endpoint: string): boolean {
    const state = this.backoffStates.get(endpoint);
    if (state === undefined) {
      return true;
    }
    if (state.attempts >= this.maxRetries) {
      return false;
    }
    const delay = this.getBackoffDelay(endpoint);
    return Date.now() - state.lastAttempt >= delay;
  }

  /**
   * Add a listener for hash events.
   */
  addHashListener(listener: HashEventListener): void {
    this.hashListeners.add(listener);
  }

  /**
   * Remove a hash listener.
   */
  removeHashListener(listener: HashEventListener): void {
    this.hashListeners.delete(listener);
  }

  /**
   * Emit a hash event to all listeners.
   */
  private emitHashEvent(event: RelayHashEvent): void {
    for (const listener of this.hashListeners) {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in hash event listener:', error);
      }
    }
  }

  /**
   * Process a hash: fetch message, signatures, keys, decrypt and update graph.
   * Optimized for mobile with backoff and retry logic.
   */
  async processHash(
    hash: string,
    options: ProcessHashOptions = {},
  ): Promise<ProcessHashResult> {
    const {
      fetchMessage = true,
      fetchSignatures = true,
      fetchKeys = true,
      decryptAndUpdateGraph = true,
    } = options;

    const result: ProcessHashResult = {
      hash,
      messageFetched: false,
      messageDecrypted: false,
      graphUpdated: false,
      signaturesFetched: 0,
      keysFetched: 0,
    };

    if (!this.isOnline) {
      result.error = 'Network offline';
      return result;
    }

    const relays = getStoredRelays().filter((r) => r.enabled);
    if (relays.length === 0) {
      result.error = 'No enabled relays';
      return result;
    }

    try {
      // Fetch message with retry and backoff
      let msgChiffre: MsgChiffre | null = null;
      if (fetchMessage) {
        for (const relay of relays) {
          if (!this.shouldRetry(relay.endpoint)) {
            continue;
          }
          try {
            msgChiffre = await getMessageByHash(relay.endpoint, hash);
            result.messageFetched = true;
            this.recordSuccess(relay.endpoint);
            break;
          } catch (error) {
            this.recordFailure(relay.endpoint);
            console.warn(
              `Failed to fetch message from ${relay.endpoint}:`,
              error,
            );
            continue;
          }
        }
      }

      // Fetch signatures with retry and backoff
      if (fetchSignatures) {
        for (const relay of relays) {
          if (!this.shouldRetry(relay.endpoint)) {
            continue;
          }
          try {
            const sigs = await getSignatures(relay.endpoint, hash);
            result.signaturesFetched += sigs.length;
            this.recordSuccess(relay.endpoint);
          } catch (error) {
            this.recordFailure(relay.endpoint);
            console.warn(
              `Failed to fetch signatures from ${relay.endpoint}:`,
              error,
            );
            continue;
          }
        }
      }

      // Fetch keys with retry and backoff
      let allKeys: MsgCle[] = [];
      if (fetchKeys) {
        for (const relay of relays) {
          if (!this.shouldRetry(relay.endpoint)) {
            continue;
          }
          try {
            const keys = await getKeys(relay.endpoint, hash);
            allKeys.push(...keys);
            this.recordSuccess(relay.endpoint);
          } catch (error) {
            this.recordFailure(relay.endpoint);
            console.warn(
              `Failed to fetch keys from ${relay.endpoint}:`,
              error,
            );
            continue;
          }
        }
        result.keysFetched = allKeys.length;
      }

      // Decrypt and update graph
      if (
        decryptAndUpdateGraph &&
        msgChiffre !== null &&
        this.identity !== null &&
        allKeys.length > 0
      ) {
        const decrypted = await tryDecryptWithKeys(
          msgChiffre,
          allKeys,
          this.identity,
        );
        if (decrypted !== null) {
          result.messageDecrypted = true;

          // Validate message
          const isValid = await validateDecryptedMessage(
            decrypted,
            async (h: string): Promise<MsgSignature[]> => {
              const allSigs: MsgSignature[] = [];
              for (const relay of relays) {
                if (!this.shouldRetry(relay.endpoint)) {
                  continue;
                }
                try {
                  const sigs = await getSignatures(relay.endpoint, h);
                  allSigs.push(...sigs);
                  this.recordSuccess(relay.endpoint);
                } catch {
                  this.recordFailure(relay.endpoint);
                  continue;
                }
              }
              return allSigs;
            },
          );

          if (isValid) {
            updateGraphFromMessage(decrypted, this.graphResolver);
            result.graphUpdated = true;
          }
        }
      }
    } catch (error) {
      result.error =
        error instanceof Error ? error.message : String(error);
    }

    return result;
  }

  /**
   * Start polling for new hashes (pull-based).
   * Polls keys in window to detect new hashes.
   * Also polls signatures and messages to detect all types of objects.
   */
  startPolling(
    intervalMs: number = 60000,
    windowStart?: number,
    windowEnd?: number,
  ): void {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;
    const start = windowStart ?? Date.now() - 3600000; // 1 hour default
    const end = windowEnd ?? Date.now();

    const poll = async (): Promise<void> => {
      try {
        const relays = getStoredRelays().filter((r) => r.enabled);
        for (const relay of relays) {
          try {
            // Poll keys (messages)
            const { getKeysInWindow } = await import('../utils/relay');
            const keys = await getKeysInWindow(relay.endpoint, start, end);
            for (const key of keys) {
              this.emitHashEvent({
                hash: key.hash_message,
                relay: relay.endpoint,
                timestamp: Date.now(),
                objectType: 'message',
                source: 'polling',
              });
            }

            // Poll signatures (if relay supports it)
            // Note: Most relays don't expose /signatures?start=&end=, but we can
            // detect new signatures when processing hashes
          } catch (error) {
            console.error(`Error polling relay ${relay.endpoint}:`, error);
          }
        }
      } catch (error) {
        console.error('Error in polling loop:', error);
      }
    };

    // Initial poll
    void poll();

    // Periodic polling
    this.pollingInterval = setInterval(() => {
      void poll();
    }, intervalMs);
  }

  /**
   * Stop polling.
   */
  stopPolling(): void {
    if (this.pollingInterval !== null) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
  }

  /**
   * Manually trigger processing of a hash (e.g., from manual trigger).
   * Supports specifying object type for better handling.
   */
  async triggerHashProcessing(
    hash: string,
    relay: string,
    options?: ProcessHashOptions,
    objectType?: RelayObjectType,
  ): Promise<ProcessHashResult> {
    // Emit event first
    this.emitHashEvent({
      hash,
      relay,
      timestamp: Date.now(),
      objectType: objectType ?? 'unknown',
      source: 'manual',
    });

    // Process hash
    return await this.processHash(hash, options);
  }

  /**
   * Process hash for specific object types (signatures, contrats, membres, pairs, actions, champs).
   * Optimizes fetching based on object type.
   */
  async processHashByType(
    hash: string,
    objectType: RelayObjectType,
    options?: ProcessHashOptions,
  ): Promise<ProcessHashResult> {
    // Adjust options based on object type
    const optimizedOptions: ProcessHashOptions = {
      fetchMessage: true,
      fetchSignatures: objectType === 'signature' || options?.fetchSignatures !== false,
      fetchKeys: objectType !== 'signature' || options?.fetchKeys !== false,
      decryptAndUpdateGraph: objectType !== 'signature' || options?.decryptAndUpdateGraph !== false,
      ...options,
    };

    return await this.processHash(hash, optimizedOptions);
  }

  /**
   * Cleanup: stop polling, remove listeners.
   */
  cleanup(): void {
    this.stopPolling();
    this.hashListeners.clear();
    this.backoffStates.clear();
  }
}
